import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UserGroupIcon,
    BriefcaseIcon,
    ChartBarIcon,
    ArrowRightIcon,
    PlusCircleIcon,
    MagnifyingGlassIcon,
    BarsArrowDownIcon,
    PencilSquareIcon,
    TrashIcon,
    ChevronDownIcon,
    ChevronUpIcon
} from '@heroicons/react/24/outline';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

// Mock data for a comprehensive list of teams
const teamsData = [
    {
        id: 'T001',
        name: 'Technology',
        members: 150,
        openPositions: 8,
        avgTenure: '2.5 years',
        demographics: { male: 100, female: 50 },
        lead: 'John Smith',
        budget: '₹50,00,000',
        performance: 'Excellent'
    },
    {
        id: 'T002',
        name: 'Sales',
        members: 120,
        openPositions: 5,
        avgTenure: '3.1 years',
        demographics: { male: 60, female: 60 },
        lead: 'Jane Doe',
        budget: '₹40,00,000',
        performance: 'Good'
    },
    {
        id: 'T003',
        name: 'Human Resources',
        members: 25,
        openPositions: 2,
        avgTenure: '4.0 years',
        demographics: { male: 10, female: 15 },
        lead: 'Emily White',
        budget: '₹10,00,000',
        performance: 'Good'
    },
    {
        id: 'T004',
        name: 'Marketing',
        members: 40,
        openPositions: 3,
        avgTenure: '1.8 years',
        demographics: { male: 25, female: 15 },
        lead: 'David Lee',
        budget: '₹15,00,000',
        performance: 'Excellent'
    },
    {
        id: 'T005',
        name: 'Operations',
        members: 85,
        openPositions: 6,
        avgTenure: '2.9 years',
        demographics: { male: 50, female: 35 },
        lead: 'Sarah Connor',
        budget: '₹35,00,000',
        performance: 'Average'
    },
];

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100, damping: 10 } }
};

const TeamOverview = () => {
    const [teams, setTeams] = useState(teamsData);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('ascending');

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortKey(key);
            setSortDirection('ascending');
        }
    };

    const sortedTeams = [...teams].sort((a, b) => {
        if (sortKey) {
            const aValue = a[sortKey];
            const bValue = b[sortKey];
            if (aValue < bValue) return sortDirection === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'ascending' ? 1 : -1;
        }
        return 0;
    });

    const filteredTeams = sortedTeams.filter(team =>
        team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.lead.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-pink-100 p-8">
            <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto"
            >
                {/* Header & Controls Section */}
                <div className="flex items-center justify-between mb-8 pb-4 border-b-2 border-purple-300">
                    <h1 className="text-4xl font-extrabold text-purple-800 tracking-tight">
                        Team Management
                    </h1>
                    <div className="flex items-center space-x-4">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search teams..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 shadow-sm"
                            />
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors"
                        >
                            <PlusCircleIcon className="h-5 w-5" />
                            <span className="font-semibold">Add Team</span>
                        </motion.button>
                    </div>
                </div>
            </motion.div>

            {/* Team Cards Grid */}
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto"
            >
                <AnimatePresence>
                    {filteredTeams.map((team) => (
                        <motion.div
                            key={team.id}
                            variants={itemVariants}
                            layout
                            className="bg-white rounded-3xl shadow-xl p-6 transform transition-transform duration-300 hover:scale-[1.02] cursor-pointer"
                        >
                            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                                        <UserGroupIcon className="h-6 w-6" />
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-800">{team.name}</h2>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                        <PencilSquareIcon className="h-5 w-5 text-gray-500 hover:text-blue-500" />
                                    </motion.button>
                                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                        <TrashIcon className="h-5 w-5 text-gray-500 hover:text-red-500" />
                                    </motion.button>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
                                <div className="flex items-center space-x-2">
                                    <UserGroupIcon className="h-5 w-5 text-gray-400" />
                                    <span>{team.members} Members</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <BriefcaseIcon className="h-5 w-5 text-gray-400" />
                                    <span>{team.openPositions} Open Positions</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <ChartBarIcon className="h-5 w-5 text-gray-400" />
                                    <span>Avg. Tenure: {team.avgTenure}</span>
                                </div>
                            </div>

                            <div className="mt-6 border-t border-gray-100 pt-4">
                                <h3 className="text-md font-semibold text-gray-800 mb-2">Team Demographics</h3>
                                <div className="h-40 w-full flex items-center justify-center">
                                    <Pie
                                        data={{
                                            labels: ['Male', 'Female'],
                                            datasets: [{
                                                data: [team.demographics.male, team.demographics.female],
                                                backgroundColor: ['#4C51BF', '#EC4899'],
                                                borderColor: '#ffffff',
                                                borderWidth: 2,
                                            }],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { position: 'right' },
                                                tooltip: {
                                                    callbacks: {
                                                        label: (context) => {
                                                            const label = context.label || '';
                                                            const value = context.parsed;
                                                            return `${label}: ${value}`;
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default TeamOverview;